// @react
import React from 'react'
// @libraries
import clsx from 'clsx'
import { makeStyles, Theme } from '@material-ui/core/styles'
// @common
import { ONBOARDING_STEPS } from 'modules/onBoarding/data/config'
// @components
import Colors from '@bit/atd.web.colors'
import Container from '@bit/atd.web.container'
import Grid from '@bit/atd.web.grid'
import LinearProgressBar from '@bit/atd.web.linear-progress-bar'
import { GET_USER } from 'graphql/queries/user.query'
import { GetUserQuery, GetUserQueryVariables } from 'lib/graphql/graphqlTypes'
import { useQuery } from '@apollo/client'
import { vsprintf } from 'sprintf-js'
import strings from '../../../../../lib/constants/strings'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    maxWidth: '768px',
    margin: '0 auto',
  },
  logoBar: {
    backgroundColor: '#fff',
    borderBottom: '2px solid',
    borderImageSlice: 1,
    borderWidth: '2px',
    borderImageSource: `linear-gradient(90deg, #FFB700 0%, #FF4D00 52.98%, #C10013 98.75%)`,
    '& $container': {
      padding: '12px 0',
      [theme.breakpoints.up('sm')]: {
        padding: '16px 0',
      },
    },
    '& svg': {
      height: '29px',
      width: '104px',
      [theme.breakpoints.up('sm')]: {
        height: '45px',
        width: '162px',
      },
    },
  },
  stepper: {
    backgroundColor: '#fff',
    zIndex: 9,
    borderBottom: `1px solid ${Colors.gray300}`,
    position: 'sticky',
    top: 0,
    [theme.breakpoints.up('sm')]: {
      position: 'relative',
    },
    '& $container': {
      padding: '16px 0',
      [theme.breakpoints.up('sm')]: {
        padding: '24px 0',
      },
      [theme.breakpoints.up('md')]: {
        padding: '32px 0',
      },
    },
  },
  stepperCount: {
    color: '#424140',
    display: 'block',
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '16px',
    letterSpacing: '0.4px',
    margin: 0,
    textTransform: 'uppercase',
    [theme.breakpoints.up('sm')]: {
      fontSize: '14px',
      margin: '0 0 4px',
    },
    '& span': {
      color: '#999999',
    },
  },
  stepperTitle: {
    color: '#2E2D31',
    margin: '0 0 8px',
    [theme.breakpoints.down('xs')]: {
      fontSize: '22px',
      lineHeight: '32px',
    },
  },
  stepperDescription: {
    color: '#424140',
    fontSize: ' 18px',
    lineHeight: '26px',
    margin: '0',
  },
  stepperHasProgressed: {
    border: `none`,
    '& $stepperTitle': {
      marginBottom: '0',
    },
  },
}))

const normalise = (value: number, MIN: number, MAX: number) =>
  ((value - MIN) * 100) / (MAX - MIN)

type PropsType = {
  id: number
}
const OnboardingHeader = ({ id }: PropsType) => {
  const classes = useStyles()

  const { loading: isLoading, data } = useQuery<
    GetUserQuery,
    GetUserQueryVariables
  >(GET_USER, { fetchPolicy: 'cache-first' })

  const userSteps = [...ONBOARDING_STEPS]
  data?.user?.membership.isMember && userSteps.pop()

  const headerMessage = () => {
    let title
    let desc
    if (isLoading) {
      return <></>
    }

    if (data?.user?.id && id === 0) {
      title = vsprintf(strings.ONBOARDING.WELCOME_USER, [
        data.user.profile.firstName,
      ])
      desc = strings.ONBOARDING.WELCOME_DESCRIPTION
    } else {
      title = userSteps[id].title || ''
      desc = userSteps[id].description || ''
    }

    return (
      <>
        <h1 className={clsx(classes.stepperTitle)}>{title}</h1>
        <p className={clsx(classes.stepperDescription)}>{desc}</p>
      </>
    )
  }

  return (
    <>
      {/* Logo Bar */}
      <div className={classes.logoBar}>
        <Container>
          <Grid className={classes.container}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 567.23 158.94">
              <defs>
                <linearGradient
                  id="linear-gradient"
                  x1="123.12"
                  x2="331.02"
                  y1="370.86"
                  y2="295.19"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0" stopColor="#ffb800" />
                  <stop offset="0.17" stopColor="#ff9000" />
                  <stop offset="0.39" stopColor="#ff6000" />
                  <stop offset="0.5" stopColor="#ff4e00" />
                  <stop offset="0.6" stopColor="#f84502" />
                  <stop offset="0.77" stopColor="#e52d09" />
                  <stop offset="0.97" stopColor="#c60513" />
                  <stop offset="1" stopColor="#c20014" />
                </linearGradient>
              </defs>
              <path
                fill="#5a5a5a"
                d="M401.25 341.72l-2.32-6.36h-9.13l-2.2 6.12H383l9.07-24.24H397l8.85 23.65zm-6.9-19.45l-3.29 9.31h6.58zm20.08 19.53a14.64 14.64 0 01-6.58-1.65l.56-3.5a11.71 11.71 0 006.17 2c1.81 0 2.79-.67 2.79-1.76s-.73-1.67-3.43-2.34c-4.09-1.05-5.81-2.24-5.81-5.32 0-2.9 2.31-5 6.23-5a14.23 14.23 0 015.91 1.2l-.55 3.49a11.61 11.61 0 00-5.36-1.53c-1.57 0-2.35.59-2.35 1.53s.64 1.4 3.15 2.07c4.38 1.12 6.09 2.31 6.09 5.49s-2.38 5.32-6.82 5.32zm15.71 0a14.64 14.64 0 01-6.58-1.65l.56-3.5a11.73 11.73 0 006.16 2c1.82 0 2.8-.67 2.8-1.76s-.73-1.67-3.43-2.34c-4.09-1.05-5.8-2.24-5.8-5.32 0-2.9 2.31-5 6.22-5a14.31 14.31 0 015.92 1.2l-.57 3.49a11.58 11.58 0 00-5.35-1.53c-1.57 0-2.34.59-2.34 1.53s.63 1.4 3.15 2.07c4.37 1.12 6.09 2.31 6.09 5.49s-2.38 5.32-6.83 5.32zm17.18.03c-4.58 0-7.91-3.29-7.91-8.78s3.36-8.82 7.94-8.82 8 3.33 8 8.82-3.41 8.78-8.03 8.78zm0-14.13c-2.49 0-3.82 2.2-3.82 5.35s1.37 5.35 3.82 5.35 3.92-2.2 3.92-5.35-1.4-5.35-3.92-5.35zm18.54 14.1c-4.68 0-8-3.15-8-8.68 0-5.88 3.67-8.89 8.16-8.89a9.48 9.48 0 015.28 1.44l-.6 3.77a7 7 0 00-4.44-1.57c-2.42 0-4.17 1.79-4.17 5.15s1.89 5.17 4.45 5.17a6.63 6.63 0 004.37-1.61l.56 3.57a9.23 9.23 0 01-5.61 1.65zm11.27-20.05a2.39 2.39 0 01-2.52-2.38 2.51 2.51 0 015 0 2.39 2.39 0 01-2.48 2.38zM475 341.48v-16.9h4.2v16.9zm18.46 0l-.34-2.2a7.5 7.5 0 01-5.64 2.55 4.42 4.42 0 01-4.79-4.69c0-4.09 3.36-5.88 10.18-6.54v-.49c0-1.82-1.05-2.52-2.9-2.52a14 14 0 00-5.64 1.36l-.53-3.35a17.37 17.37 0 016.72-1.37c4.37 0 6.55 1.61 6.55 5.67v11.58zm-.59-8.43c-4.87.6-6 2-6 3.64a1.85 1.85 0 002.07 2 5.61 5.61 0 003.95-1.83zm14.8 8.71c-3.33 0-5.57-1.22-5.57-5.07V328h-2.38v-3.43h2.38v-5.35l4.13-.63v6h3.46l.53 3.43h-4v8c0 1.36.63 2.09 2.25 2.09a4.93 4.93 0 001.32-.13l.53 3.53a11.89 11.89 0 01-2.65.25zm8.11-20.01a2.39 2.39 0 01-2.52-2.38 2.51 2.51 0 015 0 2.39 2.39 0 01-2.48 2.38zm-2.13 19.73v-16.9h4.2v16.9zm15.67.35c-4.58 0-7.9-3.29-7.9-8.78s3.36-8.82 7.94-8.82 8 3.33 8 8.82-3.42 8.78-8.04 8.78zm0-14.13c-2.48 0-3.81 2.2-3.81 5.35s1.36 5.35 3.81 5.35 3.92-2.2 3.92-5.35-1.4-5.35-3.92-5.35zm21.62 13.78v-11.06c0-1.47-.63-2.48-2.17-2.48a5.62 5.62 0 00-3.84 2.14v11.4h-4.2v-16.9h3.56l.39 2.38c1.58-1.5 3.36-2.73 5.63-2.73 3.15 0 4.83 1.93 4.83 5.07v12.18zM574.17 320c-1.43 0-2.2.63-2.2 2.14v2.48h3.22l.53 3.43H572v13.47h-4.16V328h-2.46v-3.43h2.46v-2.91c0-3.74 2.37-5.14 5.84-5.14a10.16 10.16 0 011.71.14l.56 3.43a10.81 10.81 0 00-1.78-.09zM585 341.83c-4.58 0-7.9-3.29-7.9-8.78s3.36-8.82 7.94-8.82 8 3.33 8 8.82-3.41 8.78-8.04 8.78zm0-14.13c-2.48 0-3.81 2.2-3.81 5.35s1.37 5.35 3.81 5.35c2.59 0 3.92-2.2 3.92-5.35s-1.39-5.35-3.92-5.35zm15.61 4.09v9.69h-4.19v-16.9H600l.52 3.4a6.6 6.6 0 015.43-3.75l.66 4.16a7.8 7.8 0 00-6 3.4zm-205.49 27.06V379h-4.65v-20.15h-7.18v-4h18.76l.66 4zM411.36 379l-.36-2.21a7.47 7.47 0 01-5.63 2.56 4.42 4.42 0 01-4.79-4.69c0-4.09 3.36-5.88 10.18-6.55v-.48c0-1.83-1.05-2.52-2.9-2.52a13.75 13.75 0 00-5.63 1.36l-.54-3.36a17.58 17.58 0 016.73-1.36c4.37 0 6.54 1.6 6.54 5.67V379zm-.6-8.43c-4.86.59-6 2-6 3.63a1.85 1.85 0 002.07 2 5.62 5.62 0 003.95-1.82zm8.53 8.43v-24.6l4.2-.67V379zm22.78-7.66h-10.85c.21 3.29 2 4.72 4.55 4.72a9.8 9.8 0 005.81-2.1l.49 3.29a11.54 11.54 0 01-6.75 2.07c-4.72 0-8.26-2.7-8.26-8.78 0-5.5 3.29-8.82 7.69-8.82 5.08 0 7.36 3.81 7.36 8.6 0 .36-.04.77-.04 1.02zm-7.42-6.72c-1.67 0-3 1.33-3.35 4.09h6.7c-.27-2.62-1.32-4.09-3.35-4.09zM455.72 379v-11.09c0-1.47-.64-2.48-2.18-2.48a5.64 5.64 0 00-3.85 2.13V379h-4.19v-16.94h3.56l.39 2.39a8.15 8.15 0 015.63-2.73c3.15 0 4.84 1.92 4.84 5.07V379zm14.83.24c-3.33 0-5.57-1.22-5.57-5.07v-8.67h-2.37v-3.44H465v-5.35l4.13-.63v6h3.47l.52 3.44h-4v8c0 1.36.64 2.1 2.24 2.1a5.06 5.06 0 001.34-.14l.52 3.54a12.64 12.64 0 01-2.67.22zm21.67-.24h-7.66v-24.18h7.66c8.4 0 12.73 4.93 12.73 12S500.45 379 492.22 379zm0-20.34h-3v16.52h2.94c5.09 0 8.12-2.59 8.12-8.25s-3.28-8.3-8.1-8.3zm30.39 12.68h-10.85c.21 3.29 2 4.72 4.55 4.72a9.8 9.8 0 005.81-2.1l.49 3.29a11.57 11.57 0 01-6.76 2.07c-4.72 0-8.26-2.7-8.26-8.78 0-5.5 3.3-8.82 7.71-8.82 5.07 0 7.34 3.81 7.34 8.6 0 .36-.03.77-.03 1.02zm-7.42-6.72c-1.69 0-3 1.33-3.36 4.09h6.72c-.28-2.62-1.33-4.09-3.36-4.09zM534.05 379H530l-6-16.62 4.41-.56 3.78 12 3.64-11.76H540zm22.24-7.66h-10.85c.22 3.29 2 4.72 4.56 4.72a9.77 9.77 0 005.8-2.1l.49 3.29a11.51 11.51 0 01-6.75 2.07c-4.72 0-8.26-2.7-8.26-8.78 0-5.5 3.3-8.82 7.7-8.82 5.07 0 7.35 3.81 7.35 8.6 0 .36-.04.77-.04 1.02zm-7.42-6.72c-1.67 0-3 1.33-3.35 4.09h6.71c-.23-2.62-1.32-4.09-3.36-4.09zM559.89 379v-24.6l4.2-.67V379zm15.68.32c-4.59 0-7.91-3.29-7.91-8.78s3.36-8.82 7.94-8.82 8 3.32 8 8.82-3.41 8.78-8.03 8.78zm0-14.14c-2.49 0-3.81 2.21-3.81 5.36s1.36 5.34 3.81 5.34 3.92-2.2 3.92-5.34-1.4-5.36-3.92-5.36zm19.52 13.96a11.13 11.13 0 01-3.92-.66v6.36l-4.2.63v-23.41h3.57l.39 2.32a6.77 6.77 0 015.17-2.66c3.54 0 6.37 2.66 6.37 8.32 0 6.3-3.47 9.1-7.38 9.1zm-.09-13.71c-1.5 0-2.69.94-3.81 2.38v7.24a6.63 6.63 0 003.26.73c2.16 0 3.8-1.78 3.8-5.49-.02-3.29-1.37-4.86-3.25-4.86zM626 379v-11.09c0-1.47-.53-2.48-2.06-2.48-1.3 0-2.53.83-3.75 2.1V379h-4.2v-11.09c0-1.47-.55-2.48-2.1-2.48-1.29 0-2.52.83-3.74 2.1V379h-4.2v-16.94h3.57l.39 2.39c1.57-1.51 3.29-2.73 5.56-2.73s3.7 1.08 4.33 2.73a8 8 0 015.64-2.73c3.19 0 4.76 1.92 4.76 5.07V379zm22.48-7.66h-10.85c.21 3.29 2 4.72 4.55 4.72A9.8 9.8 0 00648 374l.49 3.29a11.56 11.56 0 01-6.75 2.07c-4.73 0-8.26-2.7-8.26-8.78 0-5.5 3.29-8.82 7.69-8.82 5.08 0 7.36 3.81 7.36 8.6-.01.32-.05.73-.05.98zm-7.42-6.72c-1.68 0-3 1.33-3.36 4.09h6.72c-.28-2.62-1.33-4.09-3.36-4.09zM662.13 379v-11.09c0-1.47-.64-2.48-2.17-2.48a5.6 5.6 0 00-3.85 2.13V379h-4.2v-16.94h3.57l.38 2.39a8.15 8.15 0 015.63-2.73c3.15 0 4.83 1.92 4.83 5.07V379zm14.87.24c-3.33 0-5.57-1.22-5.57-5.07v-8.67H669v-3.44h2.38v-5.35l4.13-.63v6H679l.52 3.44h-4v8c0 1.36.63 2.1 2.24 2.1a5 5 0 001.33-.14l.52 3.54a12.53 12.53 0 01-2.61.22z"
                transform="translate(-112.39 -226.53)"
              />
              <path
                fill="url(#linear-gradient)"
                d="M323 233.51v42.16c-40.17 9.21-78.36 19.12-78.36 19.12l-.07-43.66s-13.78 2.71-28.14 7v43.74l-25.25 6.23c-5.38-17.2-19.36-28.36-36.45-28.36a93.13 93.13 0 00-36.65 7.43s1.07 3.85 2.76 8.16 3.43 7.86 3.43 7.86c6.29-2.86 15.8-6 25.25-6 9.25 0 17.32 7 17.32 16.84-33.33 7.53-54.49 18.15-54.49 35.86 0 20.22 14.42 30.67 42.56 30.67 16.76 0 30-2.89 38.09-7.21v-48.66l23.44-7.95v35.32c0 14.95 8.84 28.12 29.24 28.12a35.1 35.1 0 0013.91-2.71s.32-3.48.32-8.74-.32-8.56-.32-8.56a17.5 17.5 0 01-6 1.09c-6.34 0-9-4-9-11.37v-42.73s19.86-7 39.07-13.31C270.42 301.19 266 321.6 266 333.48c0 32.47 18.37 47.16 47.79 47.16a92.59 92.59 0 0037.21-7.21v-146.9a281.57 281.57 0 00-28 6.98zM166.7 363.14a24.24 24.24 0 01-10.32 2c-11.14 0-17.62-3.74-17.62-13 0-7.12 8.09-12.7 27.94-18.43zm156.3-1.8a23.55 23.55 0 01-8.64 1.42c-14.24 0-20.54-12.09-20.54-33.17 0-16.46 12-29.61 29.18-34.44z"
                transform="translate(-112.39 -226.53)"
              />
            </svg>
          </Grid>
        </Container>
      </div>

      {/* Header */}
      <header
        className={clsx(classes.stepper, {
          [classes.stepperHasProgressed]: id !== 0,
        })}
      >
        <Container>
          <Grid className={classes.container}>
            {id !== 0 && (
              <span className={clsx(classes.stepperCount)}>
                Step {id}
                <span> / {userSteps.length - 1}</span>
              </span>
            )}
            {headerMessage()}
          </Grid>
        </Container>

        {/* Progress Bar */}
        {id !== 0 && (
          <LinearProgressBar
            size="small"
            value={normalise(id, 0, userSteps.length - 1)}
          />
        )}
      </header>
    </>
  )
}

export default OnboardingHeader
